import { useEffect, useState, Fragment } from "react";
import api, { eur } from "@/lib/api";
import { PageHeader, Kpi, Card, Btn } from "@/components/common";
import { subLabel } from "@/lib/expenseConstants";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid, LineChart, Line } from "recharts";
import { TrendingUp, TrendingDown, Minus, FileDown, ChevronDown, ChevronRight } from "lucide-react";
import { exportAnnualPDF } from "@/lib/annualPdf";
import { useTaxForecast, MiraRows, TaxForecastCard } from "@/components/TaxForecast";

const MONTHS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

export default function Annuale() {
  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1];
  const [year, setYear] = useState(currentYear);
  const [all, setAll] = useState({});
  const [cats, setCats] = useState([]);
  const [adminCats, setAdminCats] = useState([]);
  const [openStruct, setOpenStruct] = useState({});
  const [maCutoff, setMaCutoff] = useState("2026-08-21");
  const [macData, setMacData] = useState(null);
  const [revPeriod, setRevPeriod] = useState("year");
  const [occStruct, setOccStruct] = useState("all");
  const [occPeriod, setOccPeriod] = useState("year");
  const [tx, reloadTx] = useTaxForecast(year);

  useEffect(() => {
    Promise.all(years.map((y) => api.get(`/annual-report?year=${y}`).then((r) => [y, r.data])))
      .then((entries) => setAll(Object.fromEntries(entries)));
  }, []);
  useEffect(() => { api.get("/guesthouse-categories").then((r) => setCats(r.data)); }, []);
  useEffect(() => { api.get("/administration-categories").then((r) => setAdminCats(r.data)); }, []);
  useEffect(() => {
    if (!maCutoff) { setMacData(null); return; }
    api.get(`/ma-commissions?year=${year}&cutoff=${maCutoff}`).then((r) => setMacData(r.data)).catch(() => setMacData(null));
  }, [year, maCutoff]);

  const data = all[year];
  const curr = all[currentYear];
  const next = all[currentYear + 1];
  const prevData = all[year - 1];
  const s = data?.spese;
  const catLabel = (v) => cats.find((c) => c.value === v)?.label || subLabel(v);
  const admLabel = (v) => adminCats.find((c) => c.value === v)?.label || v;
  const ghRows = s ? Object.entries(s.guesthouse).filter(([k]) => k !== "total") : [];
  const admRows = s ? Object.entries(s.amministrazione || {}).filter(([k]) => k !== "total") : [];
  const admTotal = s ? (s.amministrazione?.total || 0) : 0;
  const ADMIN_COLORS = ["#6366f1", "#f59e0b", "#10b981", "#ef4444", "#0ea5e9", "#14b8a6", "#a855f7"];
  const adminTrend = (data?.admin_monthly || []).map((m) => {
    const row = { mese: MONTHS[m.month - 1] };
    adminCats.forEach((c) => { row[c.label] = m[c.value] || 0; });
    return row;
  });
  const adminHasData = adminTrend.some((r) => adminCats.some((c) => (r[c.label] || 0) > 0));
  const staffTrend = (data?.staff_breakdown?.monthly || []).map((m) => ({ mese: MONTHS[m.month - 1], Stipendi: m.salaries || 0, Bonus: m.bonuses || 0 }));
  const staffHasData = staffTrend.some((r) => r.Stipendi > 0 || r.Bonus > 0);
  const mac = macData || data?.ma_commissions;
  const pc = data?.property_costs;
  const monthlyData = data?.monthly || [];
  const revData = (revPeriod === "year" ? monthlyData : monthlyData.filter((m) => String(m.month) === revPeriod))
    .map((m) => ({ mese: MONTHS[m.month - 1], Fatturato: m.entrate, Netto: m.netto }));
  const occStructs = data?.occupancy_detail?.structures || [];
  const selStructs = occStruct === "all" ? occStructs : occStructs.filter((s) => s.structure_id === occStruct);
  const occMonths = Array.from({ length: 12 }, (_, i) => {
    let o = 0, t = 0; selStructs.forEach((s) => { o += s.months[i].occupied; t += s.months[i].total; });
    return { mese: MONTHS[i], Occupancy: t ? Math.round((o / t) * 1000) / 10 : 0 };
  });
  const roomRows = [];
  selStructs.forEach((s) => s.rooms.forEach((r) => {
    if (occPeriod === "year") roomRows.push({ struct: s.name, name: r.name, occ: r.year_occupied, empty: r.year_empty, pct: r.year_pct });
    else { const bm = r.by_month[Number(occPeriod) - 1]; roomRows.push({ struct: s.name, name: r.name, occ: bm.occupied, empty: bm.total - bm.occupied, pct: bm.pct }); }
  }));
  let occSumO = 0, occSumT = 0;
  selStructs.forEach((s) => {
    if (occPeriod === "year") { occSumO += s.year_occupied; occSumT += s.year_total; }
    else { const i = Number(occPeriod) - 1; occSumO += s.months[i].occupied; occSumT += s.months[i].total; }
  });
  const occAvg = occSumT ? Math.round((occSumO / occSumT) * 1000) / 10 : 0;
  const totEmpty = roomRows.reduce((a, r) => a + r.empty, 0);

  return (
    <div>
      <PageHeader title="Annuale" subtitle={`Panoramica generale · anno ${year}`}>
        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="year-toggle">
          {years.map((y) => (
            <button
              key={y}
              data-testid={`year-${y}`}
              onClick={() => setYear(y)}
              className={`px-4 py-2 text-sm font-semibold transition-colors ${year === y ? "bg-sky-700 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
            >
              {y}
            </button>
          ))}
        </div>
        <Btn variant="secondary" data-testid="export-annual-pdf" onClick={() => data && exportAnnualPDF(data, year, mac)}><FileDown size={16} /> Esporta PDF</Btn>
      </PageHeader>

      {curr && next && (
        <Card className="p-5 mb-6" data-testid="yoy-compare">
          <h3 className="font-heading font-bold text-slate-900 mb-1">Confronto {currentYear} vs {currentYear + 1}</h3>
          <p className="text-xs text-slate-400 mb-4">Variazione dei principali indicatori sull'anno successivo</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { k: "Entrate", cur: curr.entrate, nx: next.entrate, fmt: eur },
              { k: "Netto", cur: curr.netto, nx: next.netto, fmt: eur },
              { k: "Prenotazioni", cur: curr.bookings_count, nx: next.bookings_count, fmt: (v) => v },
              { k: "Cash incassato", cur: curr.cash_collected, nx: next.cash_collected, fmt: eur },
              { k: "Occupancy media", cur: curr.occupancy_trend.avg, nx: next.occupancy_trend.avg, fmt: (v) => `${v}%` },
            ].map((m) => {
              const delta = m.cur === 0 ? (m.nx > 0 ? 100 : 0) : Math.round(((m.nx - m.cur) / Math.abs(m.cur)) * 100);
              const up = m.nx > m.cur, flat = m.nx === m.cur;
              const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
              const color = flat ? "text-slate-400 bg-slate-100" : up ? "text-emerald-600 bg-emerald-50" : "text-red-600 bg-red-50";
              return (
                <div key={m.k} className="rounded-lg border border-slate-200 p-3" data-testid={`yoy-${m.k}`}>
                  <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">{m.k}</div>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-slate-400">{currentYear}</span>
                    <span className="tabular font-medium text-slate-600">{m.fmt(m.cur)}</span>
                  </div>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-slate-500">{currentYear + 1}</span>
                    <span className="tabular font-bold text-slate-900">{m.fmt(m.nx)}</span>
                  </div>
                  <div className={`mt-2 inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${color}`}>
                    <Icon size={13} /> {delta > 0 ? "+" : ""}{delta}%
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {!data ? (
        <p className="text-slate-400 text-sm py-12 text-center">Caricamento dati annuali…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-6" data-testid="annual-kpis">
            <Kpi testid="a-camere" label="Camere" value={data.camere} />
            <Kpi testid="a-arrivi" label="Arrivi" value={data.arrivi} accent="text-sky-700" />
            <Kpi testid="a-partenze" label="Partenze" value={data.partenze} accent="text-amber-600" />
            <Kpi testid="a-pren" label="Prenotazioni" value={data.bookings_count} accent="text-slate-900" />
            <Kpi testid="a-cancellate" label="Cancellate" value={data.cancellate} accent="text-red-500" />
            <Kpi testid="a-entrate" label="Entrate" value={eur(data.entrate)} accent="text-slate-900" />
            <Kpi testid="a-cash" label="Cash incassato" value={eur(data.cash_collected)} accent="text-teal-600" />
            <Kpi testid="a-netto" label="Netto" value={eur(data.netto)} accent={data.netto >= 0 ? "text-emerald-600" : "text-red-600"} />
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            <Card className="p-5" data-testid="annual-occupancy-trend">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <h3 className="font-heading font-bold text-slate-900">Occupancy annuale</h3>
                <span className="text-sm font-semibold text-sky-700">Media {occAvg}%</span>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                <select data-testid="occ-struct" value={occStruct} onChange={(e) => setOccStruct(e.target.value)} className="px-2 py-1.5 rounded-md border border-slate-300 text-sm bg-white">
                  <option value="all">Tutte le strutture</option>
                  {occStructs.map((s) => <option key={s.structure_id} value={s.structure_id}>{s.name}</option>)}
                </select>
                <select data-testid="occ-period" value={occPeriod} onChange={(e) => setOccPeriod(e.target.value)} className="px-2 py-1.5 rounded-md border border-slate-300 text-sm bg-white">
                  <option value="year">Annuale</option>
                  {MONTHS.map((m, i) => <option key={i} value={String(i + 1)}>{m}</option>)}
                </select>
              </div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={occMonths} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                    <XAxis dataKey="mese" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} unit="%" width={38} />
                    <Tooltip formatter={(v) => `${v}%`} cursor={{ fill: "#f8fafc" }} />
                    <Bar dataKey="Occupancy" fill="#0ea5e9" radius={[4, 4, 0, 0]} maxBarSize={40} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="text-xs text-slate-400 mt-3 mb-1">Giorni occupati / vuoti per camera · {occPeriod === "year" ? "anno intero" : MONTHS[Number(occPeriod) - 1]}</p>
              <div className="overflow-x-auto max-h-72 overflow-y-auto">
                <table className="w-full text-sm" data-testid="occ-room-table">
                  <thead className="sticky top-0 bg-white">
                    <tr className="text-[11px] uppercase text-slate-500 border-b border-slate-200">
                      <th className="text-left py-2">Camera</th>
                      <th className="text-right py-2">Occupati</th>
                      <th className="text-right py-2">Vuoti</th>
                      <th className="text-right py-2">Occ. %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roomRows.map((r, i) => (
                      <tr key={i} className="border-b border-slate-50" data-testid={`occ-room-${i}`}>
                        <td className="py-1.5 text-slate-700">{occStruct === "all" && <span className="text-slate-400 text-xs">{r.struct} · </span>}{r.name}</td>
                        <td className="py-1.5 text-right tabular">{r.occ}</td>
                        <td className="py-1.5 text-right tabular text-amber-600">{r.empty}</td>
                        <td className="py-1.5 text-right tabular font-medium">{r.pct}%</td>
                      </tr>
                    ))}
                    {roomRows.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-slate-400">Nessuna camera</td></tr>}
                  </tbody>
                  {roomRows.length > 0 && (
                    <tfoot>
                      <tr className="border-t-2 border-slate-300 font-bold">
                        <td className="py-2 text-slate-900">Media / Totale</td>
                        <td className="py-2 text-right tabular">{occSumO}</td>
                        <td className="py-2 text-right tabular text-amber-600">{totEmpty}</td>
                        <td className="py-2 text-right tabular text-sky-700" data-testid="occ-avg">{occAvg}%</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </Card>

            <Card className="p-5" data-testid="annual-revenue-trend">
              <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                <h3 className="font-heading font-bold text-slate-900">Andamento fatturato / netto</h3>
                <select data-testid="rev-period" value={revPeriod} onChange={(e) => setRevPeriod(e.target.value)} className="px-2 py-1.5 rounded-md border border-slate-300 text-sm bg-white">
                  <option value="year">Annuale</option>
                  {MONTHS.map((m, i) => <option key={i} value={String(i + 1)}>{m}</option>)}
                </select>
              </div>
              <p className="text-xs text-slate-400 mb-4">Previsionale mensile · anno {year}</p>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={revData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                    <XAxis dataKey="mese" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : v)} width={40} />
                    <Tooltip formatter={(v) => eur(v)} cursor={{ fill: "#f8fafc" }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Fatturato" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={46} />
                    <Bar dataKey="Netto" fill="#eab308" radius={[4, 4, 0, 0]} maxBarSize={46} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="p-5">
              <h3 className="font-heading font-bold text-slate-900 mb-4">Riepilogo spese {year}</h3>
              <table className="w-full text-sm" data-testid="annual-spese">
                <tbody>
                  {[
                    ["Maldive Autentiche", s.maldive_autentiche],
                    ...Object.entries(s.manual || {}),
                    ["Affitti", s.affitti],
                  ].map(([k, v]) => (
                    <tr key={k} className="border-b border-slate-100">
                      <td className="py-2 text-slate-600">{k}</td>
                      <td className="py-2 text-right tabular font-medium">{eur(v)}</td>
                    </tr>
                  ))}
                  <tr className="border-b border-slate-100">
                    <td className="py-2 text-slate-800 font-semibold">Staff</td>
                    <td className="py-2 text-right tabular font-semibold">{eur(s.staff)}</td>
                  </tr>
                  <tr className="border-b border-slate-50">
                    <td className="py-1.5 pl-4 text-slate-500 text-xs">— Stipendi</td>
                    <td className="py-1.5 text-right tabular text-slate-500 text-xs">{eur(data.staff_breakdown?.salaries || 0)}</td>
                  </tr>
                  <tr className="border-b border-slate-50">
                    <td className="py-1.5 pl-4 text-slate-500 text-xs">— Bonus</td>
                    <td className="py-1.5 text-right tabular text-slate-500 text-xs" data-testid="annual-staff-bonus">{eur(data.staff_breakdown?.bonuses || 0)}</td>
                  </tr>
                  {Object.entries(data.staff_breakdown?.by_label || {}).map(([lbl, v]) => (
                    <tr key={`bonus-${lbl}`} className="border-b border-slate-50">
                      <td className="py-1 pl-8 text-slate-400 text-xs">· {lbl}</td>
                      <td className="py-1 text-right tabular text-slate-400 text-xs">{eur(v)}</td>
                    </tr>
                  ))}
                  <tr className="border-b border-slate-100">
                    <td className="py-2 text-slate-800 font-semibold">Spese Guesthouse</td>
                    <td className="py-2 text-right tabular font-semibold">{eur(s.guesthouse.total)}</td>
                  </tr>
                  {ghRows.map(([k, v]) => (
                    <tr key={k} className="border-b border-slate-50">
                      <td className="py-1.5 pl-4 text-slate-500 text-xs">— {catLabel(k)}</td>
                      <td className="py-1.5 text-right tabular text-slate-500 text-xs">{eur(v)}</td>
                    </tr>
                  ))}
                  <tr className="border-b border-slate-100">
                    <td className="py-2 text-slate-800 font-semibold">Amministrazione</td>
                    <td className="py-2 text-right tabular font-semibold">{eur(admTotal)}</td>
                  </tr>
                  {admRows.map(([k, v]) => (
                    <tr key={`adm-${k}`} className="border-b border-slate-50">
                      <td className="py-1.5 pl-4 text-slate-500 text-xs">— {admLabel(k)}</td>
                      <td className="py-1.5 text-right tabular text-slate-500 text-xs">{eur(v)}</td>
                    </tr>
                  ))}
                  <MiraRows mira={s.mira} tx={tx} />
                  {(s.marketing > 0 || data.marketing_incassato > 0) && (
                    <tr className="border-b border-slate-100" data-testid="annual-marketing">
                      <td className="py-2 text-slate-600">Marketing <span className="text-xs text-slate-400">(fotografo + costo magliette · incassato {eur(data.marketing_incassato)} nelle entrate)</span></td>
                      <td className="py-2 text-right tabular font-medium">{eur(s.marketing)}</td>
                    </tr>
                  )}
                  <tr className="font-bold">
                    <td className="py-2.5">Totale spese</td>
                    <td className="py-2.5 text-right tabular text-red-600">{eur(s.total)}</td>
                  </tr>
                </tbody>
              </table>
              {tx && <p className="text-[11px] text-amber-700 mt-2">Tra parentesi: tasse da pagare stimate sulle prenotazioni confermate (dettaglio mese per mese sotto).</p>}
            </Card>

            <Card className="p-5">
              <h3 className="font-heading font-bold text-slate-900 mb-4">Prenotazioni per origine {year}</h3>
              <table className="w-full text-sm" data-testid="annual-origine">
                <thead>
                  <tr className="text-xs uppercase text-slate-500 border-b border-slate-200">
                    <th className="text-left py-2">Origine</th>
                    <th className="text-right py-2">Pren.</th>
                    <th className="text-right py-2">Entrate</th>
                    <th className="text-right py-2">Commissione</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(data.per_agency).filter(([, v]) => v.count > 0).map(([k, v]) => (
                    <tr key={k} className="border-b border-slate-100">
                      <td className="py-2 text-slate-700">{k}</td>
                      <td className="py-2 text-right tabular">{v.count}</td>
                      <td className="py-2 text-right tabular">{eur(v.entrate)}</td>
                      <td className="py-2 text-right tabular text-slate-600">{eur(v.commissione)}</td>
                    </tr>
                  ))}
                  {data.bookings_count === 0 && (
                    <tr><td colSpan={4} className="py-8 text-center text-slate-400">Nessuna prenotazione nell'anno</td></tr>
                  )}
                </tbody>
              </table>
            </Card>
          </div>

          <div className="mt-6"><TaxForecastCard tx={tx} monthly={monthlyData} onSaved={reloadTx} /></div>

          {mac && (
            <Card className="p-5 mt-6" data-testid="annual-commissioni">
              <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                <h3 className="font-heading font-bold text-slate-900">Commissioni versate a Maldive Autentiche {year}</h3>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-slate-500">Conguaglio: inserite entro il</label>
                  <input type="date" value={maCutoff} onChange={(e) => setMaCutoff(e.target.value)} className="border border-slate-200 rounded-md px-2 py-1 text-sm" data-testid="ma-cutoff" />
                  {maCutoff && <button onClick={() => setMaCutoff("")} className="text-xs text-slate-400 underline" data-testid="ma-cutoff-clear">tutto</button>}
                  <span className="text-sm font-bold text-sky-700 ml-2">Totale {eur(mac.total)}</span>
                </div>
              </div>
              <p className="text-xs text-slate-400 mb-4">Numero ospiti e importo versato per fascia d'età, per agenzia (incluso Diretto) e totale generale. {maCutoff ? `Solo prenotazioni inserite a calendario entro il ${maCutoff.split("-").reverse().join("/")} (esclude quelle immesse dopo, le cancellate e "commissione non dovuta").` : "Tutte le prenotazioni dell'anno (esclude cancellate e commissione non dovuta)."}</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="annual-commissioni-table">
                  <thead>
                    <tr className="text-[11px] uppercase text-slate-500 border-b border-slate-200">
                      <th className="text-left py-2">Agenzia</th>
                      <th className="text-right py-2">Adulti 13+</th>
                      <th className="text-right py-2">5–12</th>
                      <th className="text-right py-2">2–4</th>
                      <th className="text-right py-2">0–1</th>
                      <th className="text-right py-2">Versato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mac.agencies.map((a) => (
                      <Fragment key={a.name}>
                      <tr className="border-b border-slate-100 cursor-pointer hover:bg-slate-50" data-testid={`comm-row-${a.name}`} onClick={() => setOpenStruct((o) => ({ ...o, [`ag-${a.name}`]: !o[`ag-${a.name}`] }))}>
                        <td className="py-2 font-medium text-slate-700 flex items-center gap-1">{openStruct[`ag-${a.name}`] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}{a.name}</td>
                        <td className="py-2 text-right"><span className="tabular font-medium">{a.counts.a}</span> <span className="text-[11px] text-slate-400">({eur(a.amounts.a)})</span></td>
                        <td className="py-2 text-right"><span className="tabular font-medium">{a.counts.c}</span> <span className="text-[11px] text-slate-400">({eur(a.amounts.c)})</span></td>
                        <td className="py-2 text-right"><span className="tabular font-medium">{a.counts.c2}</span> <span className="text-[11px] text-slate-400">({eur(a.amounts.c2)})</span></td>
                        <td className="py-2 text-right"><span className="tabular font-medium">{a.counts.inf}</span> <span className="text-[11px] text-slate-400">({eur(a.amounts.inf)})</span></td>
                        <td className="py-2 text-right tabular font-semibold text-sky-700">{eur(a.total)}</td>
                      </tr>
                      {openStruct[`ag-${a.name}`] && (a.details || []).map((d, i) => (
                        <tr key={`${a.name}-${i}`} className="border-b border-slate-50 bg-slate-50/40 text-xs">
                          <td className="py-1.5 pl-6 text-slate-500">{d.guest || "—"} <span className="text-slate-400">· {d.checkin}</span></td>
                          <td className="py-1.5 text-right text-slate-500 tabular">{d.a}</td>
                          <td className="py-1.5 text-right text-slate-500 tabular">{d.c}</td>
                          <td className="py-1.5 text-right text-slate-500 tabular">{d.c2}</td>
                          <td className="py-1.5 text-right text-slate-500 tabular">{d.inf}</td>
                          <td className="py-1.5 text-right tabular text-slate-600">{eur(d.amount)}</td>
                        </tr>
                      ))}
                      </Fragment>
                    ))}
                    {mac.agencies.length === 0 && (
                      <tr><td colSpan={6} className="py-8 text-center text-slate-400">Nessuna commissione registrata nell'anno</td></tr>
                    )}
                  </tbody>
                  {mac.agencies.length > 0 && (
                    <tfoot>
                      <tr className="border-t-2 border-slate-300 font-bold">
                        <td className="py-2.5 text-slate-900">Totale generale</td>
                        <td className="py-2.5 text-right"><span className="tabular">{mac.counts.a}</span> <span className="text-[11px] text-slate-500">({eur(mac.amounts.a)})</span></td>
                        <td className="py-2.5 text-right"><span className="tabular">{mac.counts.c}</span> <span className="text-[11px] text-slate-500">({eur(mac.amounts.c)})</span></td>
                        <td className="py-2.5 text-right"><span className="tabular">{mac.counts.c2}</span> <span className="text-[11px] text-slate-500">({eur(mac.amounts.c2)})</span></td>
                        <td className="py-2.5 text-right"><span className="tabular">{mac.counts.inf}</span> <span className="text-[11px] text-slate-500">({eur(mac.amounts.inf)})</span></td>
                        <td className="py-2.5 text-right tabular text-sky-700" data-testid="annual-commissioni-total">{eur(mac.total)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </Card>
          )}

          {pc && (
            <Card className="p-5 mt-6" data-testid="annual-property-costs">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-heading font-bold text-slate-900">Costi altre strutture (pagati da Blu Lodge) {year}</h3>
                <div className="text-right">
                  <span className="text-sm font-bold text-red-600">Totale {eur(pc.total)}</span>
                  {prevData?.property_costs && (() => {
                    const pv = prevData.property_costs.total || 0; const diff = pc.total - pv;
                    const up = diff > 0;
                    return <div className={`text-xs mt-0.5 ${up ? "text-red-500" : diff < 0 ? "text-emerald-600" : "text-slate-400"}`} data-testid="pc-yoy">
                      {year - 1}: {eur(pv)} · {diff === 0 ? "invariato" : `${up ? "+" : ""}${eur(diff)}`}
                    </div>;
                  })()}
                </div>
              </div>
              <p className="text-xs text-slate-400 mb-4">Quanto Blu Lodge spende per le prenotazioni che alloggiano nelle altre strutture (Vaagali Inn, Lonu Fen Lodge, ecc.), in base al costo camera/persona per notte. Clicca una struttura per il dettaglio.</p>
              <table className="w-full text-sm" data-testid="annual-property-costs-table">
                <thead>
                  <tr className="text-[11px] uppercase text-slate-500 border-b border-slate-200">
                    <th className="text-left py-2">Struttura</th>
                    <th className="text-right py-2">Prenotazioni</th>
                    <th className="text-right py-2">Notti</th>
                    <th className="text-right py-2">Costo</th>
                  </tr>
                </thead>
                <tbody>
                  {pc.structures.map((st) => (
                    <Fragment key={st.name}>
                      <tr className="border-b border-slate-100 cursor-pointer hover:bg-slate-50" data-testid={`pc-row-${st.name}`} onClick={() => setOpenStruct((o) => ({ ...o, [st.name]: !o[st.name] }))}>
                        <td className="py-2 font-medium text-slate-700 flex items-center gap-1">{openStruct[st.name] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}{st.name}</td>
                        <td className="py-2 text-right tabular">{st.bookings}</td>
                        <td className="py-2 text-right tabular">{st.nights}</td>
                        <td className="py-2 text-right tabular font-semibold text-red-600">{eur(st.total)}</td>
                      </tr>
                      {openStruct[st.name] && (st.details || []).map((d, i) => (
                        <tr key={`${st.name}-${i}`} className="border-b border-slate-50 bg-slate-50/40 text-xs">
                          <td className="py-1.5 pl-6 text-slate-500">{d.guest || "—"} <span className="text-slate-400">· {d.origine}</span></td>
                          <td className="py-1.5 text-right text-slate-500" colSpan={1}>{d.checkin} → {d.checkout}</td>
                          <td className="py-1.5 text-right tabular text-slate-500">{d.nights}</td>
                          <td className="py-1.5 text-right tabular text-slate-600">{eur(d.cost)}</td>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                  {pc.structures.length === 0 && (
                    <tr><td colSpan={4} className="py-8 text-center text-slate-400">Nessun costo di altre strutture registrato nell'anno</td></tr>
                  )}
                </tbody>
                {pc.structures.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-slate-300 font-bold">
                      <td className="py-2.5 text-slate-900">Totale generale</td>
                      <td className="py-2.5 text-right tabular">{pc.structures.reduce((a, x) => a + x.bookings, 0)}</td>
                      <td className="py-2.5 text-right tabular">{pc.structures.reduce((a, x) => a + x.nights, 0)}</td>
                      <td className="py-2.5 text-right tabular text-red-600" data-testid="annual-property-costs-total">{eur(pc.total)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </Card>
          )}

          <Card className="p-5 mt-6" data-testid="annual-admin-chart">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-slate-900">Andamento spese Amministrazione {year}</h3>
              <span className="text-sm text-slate-500">Totale {eur(admTotal)}</span>
            </div>
            {adminHasData ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={adminTrend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="mese" tick={{ fontSize: 12, fill: "#64748b" }} />
                  <YAxis tick={{ fontSize: 12, fill: "#64748b" }} />
                  <Tooltip formatter={(v) => eur(v)} />
                  <Legend />
                  {adminCats.map((c, i) => (
                    <Bar key={c.value} dataKey={c.label} stackId="adm" fill={ADMIN_COLORS[i % ADMIN_COLORS.length]} radius={i === adminCats.length - 1 ? [4, 4, 0, 0] : 0} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="py-12 text-center text-slate-400 text-sm">Nessuna spesa di Amministrazione registrata nell'anno</div>
            )}
          </Card>

          <Card className="p-5 mt-6" data-testid="annual-staff-chart">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-slate-900">Andamento Staff {year}: stipendi e bonus</h3>
              <span className="text-sm text-slate-500">Stipendi {eur(data.staff_breakdown?.salaries || 0)} · Bonus {eur(data.staff_breakdown?.bonuses || 0)}</span>
            </div>
            {staffHasData ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={staffTrend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="mese" tick={{ fontSize: 12, fill: "#64748b" }} />
                  <YAxis tick={{ fontSize: 12, fill: "#64748b" }} />
                  <Tooltip formatter={(v) => eur(v)} />
                  <Legend />
                  <Bar dataKey="Stipendi" stackId="staff" fill="#0ea5e9" />
                  <Bar dataKey="Bonus" stackId="staff" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="py-12 text-center text-slate-400 text-sm">Nessuna spesa staff registrata nell'anno</div>
            )}
          </Card>

          <div className="mt-6 bg-slate-900 text-white rounded-md p-6 flex items-center justify-between">
            <div>
              <div className="text-sm text-slate-300 uppercase tracking-wide">Netto annuale {year}</div>
              <div className="text-xs text-slate-400 mt-1">Entrate − tutte le spese</div>
            </div>
            <div className={`font-heading text-4xl font-extrabold tabular ${data.netto >= 0 ? "text-emerald-400" : "text-red-400"}`} data-testid="annual-netto-big">
              {eur(data.netto)}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
